import { Footer } from "@/components/layout/Footer";
import { SellerCard } from "@/components/shared/SellerCard";
import { ListingCard } from "@/components/shared/ListingCard";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Store, Package, Grid3x3 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useState, useEffect } from "react";
import { useSearch } from "wouter";
import { useLocalePreferences } from "@/lib/locale-preferences";
import { calculateSubtotal, FIXED_FEE_AMOUNT } from "@/lib/pricing";
import { supabase } from "@/lib/supabase";

// Transform seller data from snake_case (database) to camelCase (components)
function transformSeller(seller: any) {
  return {
    ...seller,
    displayName: seller.store_name || seller.display_name || seller.displayName,
    shopName: seller.store_name || seller.shopName,
    avatarUrl: seller.avatar_url || seller.avatarUrl || seller.avatar || seller.profile_image_url,
    location: seller.location,
    rating: seller.rating || 0,
    reviewCount: seller.review_count || seller.reviewCount || 0,
    sellerTags: seller.seller_tags || seller.sellerTags || [],
    printerCount: seller.printer_count || seller.printerCount || 0,
    totalPrints: seller.total_prints || seller.totalPrints || 0,
    shopMode: seller.shop_mode || seller.shopMode || 'both',
    bio: seller.bio,
  };
}

// Transform listing data from snake_case (database) to camelCase (components)
function transformListing(listing: any) {
  return {
    ...listing,
    imageUrl: listing.images?.[0] || listing.image_url || listing.imageUrl,
    title: listing.title,
    description: listing.description,
    category: listing.category,
    basePrice: (listing.price || listing.base_price || listing.basePrice || 0) + 1.62 + FIXED_FEE_AMOUNT,
    shippingCost: listing.shipping_cost || listing.shippingCost || 0,
    listingType: listing.listing_type || listing.listingType,
    sellerId: listing.seller_id || listing.sellerId,
    sellerName: listing.seller_name || listing.sellerName,
    estimatedDaysMin: listing.estimated_days_min || listing.estimatedDaysMin,
    estimatedDaysMax: listing.estimated_days_max || listing.estimatedDaysMax,
    tags: listing.tags || [],
    stockQuantity: listing.stock_quantity !== undefined ? listing.stock_quantity : listing.stock,
    trackStock: listing.track_stock !== undefined ? listing.track_stock : listing.track_stock,
  };
}

export default function ExploreAll() {
  const rawSearch = useSearch();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"all" | "shops" | "models">("all");
  const [sellers, setSellers] = useState<any[]>([]);
  const [listings, setListings] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingListings, setLoadingListings] = useState(false);

  // Handle filter query parameter from URL
  useEffect(() => {
    const params = new URLSearchParams(rawSearch);
    const filter = params.get('filter');
    if (filter === 'shops' || filter === 'models') {
      setFilterType(filter);
    }
  }, [rawSearch]);

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase.from('sellers').select('*');
        if (data && !error) {
          setSellers(data.map(transformSeller));
        }
      } catch (err) {
        console.error('Error fetching sellers:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSellers();
  }, []);

  useEffect(() => {
    const fetchListings = async () => {
      try {
        setLoadingListings(true);
        const { data: listingsData, error: listingsError } = await supabase.from('listings').select('*');
        const { data: sellersData, error: sellersError } = await supabase.from('sellers').select('id, store_name, accepting_orders');

        if (listingsData && !listingsError && sellersData && !sellersError) {
          // Create maps of seller IDs to seller data
          const sellerMap = new Map(sellersData.map(s => [s.id, s.store_name]));
          const acceptingOrdersMap = new Map(sellersData.map(s => [s.id, s.accepting_orders]));

          // Transform listings with seller names
          const transformedListings = listingsData.map((listing: any) => {
            const transformed = transformListing(listing);
            transformed.sellerName = sellerMap.get(listing.seller_id) || 'Unknown Seller';
            transformed.sellerAcceptingOrders = acceptingOrdersMap.get(listing.seller_id);
            return transformed;
          });
          setListings(transformedListings);
        }
      } catch (err) {
        console.error('Error fetching listings:', err);
      } finally {
        setLoadingListings(false);
      }
    };
    fetchListings();
  }, []);

  const filteredSellers = sellers.filter((s) => {
    const q = (searchTerm || '').toLowerCase();
    const matchesSearch =
      (s.displayName || '').toLowerCase().includes(q) ||
      (s.shopName || '').toLowerCase().includes(q);
    return matchesSearch;
  });

  const filteredListings = listings.filter((l) => {
    const q = (searchTerm || '').toLowerCase();
    return (
      (l.title || '').toLowerCase().includes(q) ||
      (l.description || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen flex flex-col bg-zinc-950">
      <div className="flex-grow">
        <div className="container mx-auto px-4 py-12">
          <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-6 text-center">
            The All in One Makers Marketplace
          </h1>
          <p className="text-xl text-zinc-400 mb-8 text-center">
            Discover top shops and amazing 3D models all in one place
          </p>
          <div className="relative max-w-2xl mx-auto mb-12">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <Input
              type="text"
              placeholder="Search shops..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 pr-4 py-4 h-14 bg-zinc-900/50 border border-zinc-700 rounded-full text-lg focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <Tabs value={filterType} onValueChange={(v) => setFilterType(v as any)} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-zinc-900 border border-zinc-700">
              <TabsTrigger value="all" className="data-[state=active]:bg-primary">
                <Grid3x3 className="w-4 h-4 mr-2" />
                All
              </TabsTrigger>
              <TabsTrigger value="shops" className="data-[state=active]:bg-primary">
                <Store className="w-4 h-4 mr-2" />
                Shops
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="mt-8">
              <div className="space-y-12">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <Store className="w-6 h-6 text-primary" />
                    Featured Shops
                  </h2>
                  {isLoading ? (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="h-64 bg-zinc-800" />
                      ))}
                    </div>
                  ) : filteredSellers.length > 0 ? (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {filteredSellers.slice(0, 6).map((seller) => (
                        <SellerCard key={seller.id} seller={seller} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Store className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
                      <p className="text-zinc-400">No shops found</p>
                    </div>
                  )}
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <Package className="w-6 h-6 text-primary" />
                    Featured Models
                  </h2>
                  {loadingListings ? (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                      {[...Array(8)].map((_, i) => (
                        <Skeleton key={i} className="h-64 bg-zinc-800" />
                      ))}
                    </div>
                  ) : filteredListings.length > 0 ? (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                      {filteredListings.slice(0, 8).map((listing) => (
                        <ListingCard key={listing.id} listing={listing} sellerAcceptingOrders={listing.sellerAcceptingOrders} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Package className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
                      <p className="text-zinc-400">No models found</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="shops" className="mt-8">
              {isLoading ? (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} className="h-64 bg-zinc-800" />
                  ))}
                </div>
              ) : filteredSellers.length > 0 ? (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {filteredSellers.map((seller) => (
                    <SellerCard key={seller.id} seller={seller} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Store className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
                  <p className="text-zinc-400">No shops found</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <Footer />
    </div>
  );
}
